import { ShipmentTracking } from '@graphcommerce/graphql-mesh'
import { Image } from '@graphcommerce/image'
import { CustomerDocument, useCustomerQuery } from '@graphcommerce/magento-customer'
import { OrderItemFragment } from '@graphcommerce/magento-customer/components/OrderItem/OrderItem.gql'
import { OrderCardItemImageFragment } from '@graphcommerce/magento-customer/hooks/OrderCardItemImage.gql'
import { extendableComponent } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Box, Button } from '@mui/material'
import { default as Link, default as PageLink } from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import DownloadArtwork from '../../Artwork/DownloadArtwork'
import { ReviewProof } from '../../Artwork/ReviewProof'
import { AddReview } from '../../Reviews/AddReview'
import { ReorderItem } from '../Reorder/ReorderItem'
import { OrderItemAdditionalFragment } from './OrderItemAdditional.gql'
import { OrderStatusLabel } from './OrderStatusLabel'

type OrderItemProps = OrderItemFragment &
  OrderItemAdditionalFragment &
  Omit<OrderCardItemImageFragment, 'uid'> & {
    shipment: ShipmentTracking | null | undefined
  } & { orderId?: string; reviews?: any[]; hideStatus?: boolean }

type OwnerState = { hasOptions: boolean }
const componentName = 'OrderItem' as const
const parts = [
  'root',
  'itemWithoutOptions',
  'picture',
  'pictureSpacing',
  'image',
  'productLink',
  'itemName',
  'itemNameWithOptions',
  'itemPrice',
  'quantity',
  'rowPrice',
  'optionsList',
  'option',
] as const
const { withState } = extendableComponent<OwnerState, typeof componentName, typeof parts>(
  componentName,
  parts,
)

export function OrderItem(props: OrderItemProps) {
  const {
    product_url_key,
    selected_options,
    product_sale_price,
    quantity_ordered,
    product_name,
    product_exists,
    thumbnail,
    status,
    artwork,
    proofs,
    shipment,
    orderId,
    product_id,
    reviews,
    hideStatus,
  } = props

  const proofWorkflowStatuses = ['PROOF READY', 'PRE-PRODUCTION', 'REVISING PROOF']
  const isInOrderListPage = window.location.href.indexOf('orderId') < 0

  const productLink = `/p/${product_url_key}`

  const hasOptions = Boolean(selected_options && selected_options.length >= 1)

  const classes = withState({ hasOptions })

  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const customer = dashboard.data?.customer

  const [hasLeftReview, setHasLeftReview] = useState<boolean>(false)

  const currentUserReviewsForThisProduct = reviews?.filter(
    (review) =>
      review?.email?.toUpperCase() === customer?.email?.toUpperCase() &&
      review?.sku === product_id?.toString(),
  )

  useEffect(() => {
    const hasLeftAReviewInSessionForProduct = (): boolean => {
      const userSessionReviewedProducts = sessionStorage.getItem('userReviewedProducts')
      if (userSessionReviewedProducts && userSessionReviewedProducts.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const userSessionReviewedProductsArry = userSessionReviewedProducts.split(',')
        if (
          userSessionReviewedProductsArry.filter((item) => item === product_id?.toString())
            ?.length > 0
        ) {
          return true
        }
        return false
      }
      return false
    }

    const hasLeftReviewLocal =
      ((currentUserReviewsForThisProduct && currentUserReviewsForThisProduct?.length > 0) ||
        hasLeftAReviewInSessionForProduct()) ??
      false
    setHasLeftReview(hasLeftReviewLocal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserReviewsForThisProduct, sessionStorage])
  const hasApprovedProof = !!proofs?.find((proof) => proof?.status === 'Approved')
  const reorderAllowed = product_exists && (!artwork?.length || hasApprovedProof)

  const trackingUrl = useMemo(() => {
    if (shipment) {
      if (shipment?.carrier.includes('ups')) {
        return `https://www.ups.com/track?track=yes&trackNums=${shipment.number}`
      }
      if (shipment?.carrier.includes('fedex')) {
        return `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${shipment.number}`
      }
      return `https://www.tforcefreight.com/ltl/apps/Tracking?proNumbers=${shipment.number}`
    }
    return null
  }, [shipment])

  return (
    <div className='flex border-b-[1px] border-50-grey py-[12px] last:border-none md:py-[20px]'>
      <div className='flex w-full grow flex-col gap-[15px] md:grid md:grid-cols-1 lg:grid-cols-[20fr_50fr_30fr]'>
        <div className='mb-[10px] sm:mb-0'>
          <PageLink href={productLink} passHref>
            {thumbnail?.url && thumbnail?.label ? (
              <div className='flex w-full items-center justify-start rounded-[6px]'>
                <Box
                  component='a'
                  className=''
                  sx={{
                    overflow: 'hidden',
                    height: '100%',
                    width: '100%',
                    backgroundRepeat: 'no-repeat',
                    aspectRatio: '5/6',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '6px',
                    '@media (min-width: 960px)': {
                      minHeight: '200px',
                    },
                  }}
                >
                  <Image
                    alt={thumbnail?.label ?? ''}
                    src={thumbnail?.url ?? ''}
                    unoptimized
                    layout='fill'
                    className='h-full w-auto object-cover'
                    sx={{
                      aspectRatio: '5/6',
                      borderRadius: '6px',
                    }}
                  />
                </Box>
              </div>
            ) : (
              <Box
                sx={{
                  gridArea: `1 / 1 / 4 / 4`,
                  typography: 'caption',
                  display: 'flex',
                  textAlign: 'center',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'background.default',
                  userSelect: 'none',
                  borderRadius: '6px',
                  aspectRatio: '5/6',
                }}
              >
                <Trans id='No Image' />
              </Box>
            )}
          </PageLink>
        </div>
        <div className='md:pl-[30px]'>
          <PageLink href={productLink} passHref className='Type-XL-Bold text-tight-black'>
            {product_name}
          </PageLink>

          {status && !hideStatus && (
            <div className='Type-Large-Medium mb-2 mt-6 grid grid-cols-2 items-center justify-items-start '>
              <span className='Type-Large-Medium mr-[13px] hidden text-tight-black md:inline-block'>
                <Trans id='Order status:' />
              </span>
              <OrderStatusLabel status={status} />
            </div>
          )}
          {proofs &&
            proofs.length > 0 &&
            proofWorkflowStatuses.includes(status?.toUpperCase() ?? '') && (
              <div className='Type-Large-Medium mb-4 grid grid-cols-2 items-center justify-items-start '>
                <span className='Type-Large-Medium mr-[13px] hidden text-tight-black md:inline-block'>
                  Proof status:
                </span>
                <span
                  className={`Type-Large-Bold flex h-[26px] items-center justify-center rounded-[5px] uppercase ${
                    proofs[proofs.length - 1]?.status?.toUpperCase() === 'APPROVED'
                      ? 'text-[#2E7D32]'
                      : 'text-material-ui-dark-blue'
                  }`}
                >
                  {proofs[proofs.length - 1]?.status}
                </span>
              </div>
            )}

          <div className='Type-Large-Medium my-2 grid grid-cols-2 items-center justify-items-start '>
            <span>
              <Trans id='Quantity: ' />
            </span>
            <span className='Type-Large-Regular'>{quantity_ordered?.toLocaleString()}</span>
          </div>
          {hasOptions && (
            <div className='w-full'>
              {selected_options &&
                selected_options.map((option) => (
                  <div
                    className='mb-2 grid grid-cols-2 items-center justify-items-start'
                    key={option?.value}
                  >
                    <span className='Type-Large-Medium md:whitespace-nowrap'>
                      {option?.label}:{' '}
                    </span>
                    <span className='Type-Large-Regular'>
                      {' '}
                      {option?.value?.indexOf('#') === 0 ? (
                        <div className='ml-[6px] inline-flex items-center justify-center'>
                          <div
                            className='inline-block h-[12px] w-[32px] rounded-[3px] border-[1px] border-[#e1e1e1] align-middle'
                            style={{ backgroundColor: option?.value }}
                          />
                        </div>
                      ) : (
                        option?.value
                      )}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className='col-span-2 flex grow flex-col gap-[10px] last:mb-0 lg:col-span-1 lg:block'>
          {reorderAllowed && <ReorderItem {...props} />}
          {trackingUrl && (
            <div className='lg:mb-3'>
              <Link
                target='_blank'
                rel='noopener noreferrer'
                href={`${trackingUrl.replaceAll(' ', '')}`}
              >
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  className='w-full rounded-[5px] bg-pure-white uppercase'
                >
                  {i18n._('Track shipment')}
                </Button>
              </Link>
            </div>
          )}
          {proofs &&
            proofs?.length > 0 &&
            (proofs[proofs.length - 1]?.status.toLocaleUpperCase() === 'PENDING APPROVAL' ||
              proofs[proofs.length - 1]?.status.toLocaleUpperCase() === 'APPROVED') && (
              <ReviewProof proofs={proofs} />
            )}
          {artwork &&
            artwork?.length > 0 &&
            (!proofs ||
              proofs.length === 0 ||
              // eslint-disable-next-line no-unsafe-optional-chaining
              proofs[proofs?.length - 1]?.status.toLocaleUpperCase() !== 'APPROVED') && (
              <DownloadArtwork artworkFiles={artwork ?? []} defaultStyle={false} />
            )}
          {!hasLeftReview &&
            (status?.toUpperCase() === 'COMPLETE' ||
              status?.toUpperCase() === 'DELIVERED' ||
              status?.toUpperCase() === 'ORDER SHIPPED') && (
              <AddReview
                productLink={productLink}
                productName={product_name}
                thumbnail={thumbnail}
                productId={product_id?.toString()}
                customer={customer}
              />
            )}
          {isInOrderListPage && (
            <Link
              target='_self'
              rel='noopener noreferrer'
              href={`/account/orders/view?orderId=${orderId}`}
            >
              <Button
                variant='outlined'
                color='primary'
                size='small'
                className='w-full rounded-[5px] bg-pure-white uppercase'
              >
                {i18n._('View order details')}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
